import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  Row,
  Col,
  Alert,
  Button,
  Tabs,
  Statistic,
  DatePicker,
  Space,
  message,
} from "antd";
import {
  ReloadOutlined,
  DashboardOutlined,
  BarChartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  getAdminDashboardOverview,
  formatDashboardData,
  reconcileInfractions,
} from "../api/parkpass/dashboardOverview";
import Loading from "../components/Loading";
import AdminDashboardStats from "../components/AdminDashboardStats";
import SystemWalletsFinancial from "../components/SystemWalletsFinancial";
import TransactionStatusChart from "../components/TransactionStatusChart";
import PaymentComparisonChart from "../components/PaymentComparisonChart";
import ParkingTicketsChart from "../components/ParkingTicketsChart";
import { useSelector } from "react-redux";
import { hasPermission } from "../store/userSlice";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [reconcileDates, setReconcileDates] = useState([]);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const canSeeFinancials = useSelector((state) =>
    hasPermission(state, "citizen_transaction", "filter-all")
  );

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      const rawData = await getAdminDashboardOverview();
      const formattedData = formatDashboardData(rawData);

      setDashboardData(formattedData);
      setLastUpdated(new Date(formattedData.generatedAt));
    } catch (error) {
      console.error("Error fetching admin dashboard data:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load dashboard data"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const handleReconcile = async () => {
    if (!reconcileDates || reconcileDates.length !== 2) {
      message.warning("Veuillez sélectionner une période");
      return;
    }
    try {
      setReconcileLoading(true);
      const result = await reconcileInfractions(
        reconcileDates[0].format("YYYY-MM-DD"),
        reconcileDates[1].format("YYYY-MM-DD")
      );
      setReconcileResult(result);
      const s = result?.summary || {};
      message.success(
        `Réconciliation terminée: +${s.total_added || 0} ajoutées, ${s.total_updated || 0} mises à jour`
      );
      fetchDashboardData();
    } catch (err) {
      console.error("Reconciliation error:", err);
      message.error(
        err.response?.data?.detail || "Erreur lors de la réconciliation"
      );
    } finally {
      setReconcileLoading(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="px-6 py-4 bg-gradient-to-br from-purple-50 via-white to-blue-50 min-h-screen">
      {/* Header */}
      <Card className="mb-6 shadow-md border-t-4 border-purple-600">
        <div className="flex justify-between items-center">
          <div>
            <Title level={2} className="mb-1 text-purple-700">
              <DashboardOutlined className="mr-2" />
              Admin Dashboard
            </Title>
            {lastUpdated && (
              <Text type="secondary" className="text-sm">
                <ClockCircleOutlined className="mr-1" />
                Last updated:{" "}
                {lastUpdated.toLocaleString("fr-FR", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </Text>
            )}
          </div>
          <Button
            type="primary"
            size="large"
            icon={<ReloadOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
            className="shadow-md"
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error Loading Dashboard"
          description={error}
          type="error"
          showIcon
          closable
          onClose={() => setError(null)}
          className="mb-4"
        />
      )}

      {/* Dashboard Content */}
      {dashboardData && (
        <Tabs
          defaultActiveKey="1"
          size="large"
          className="dashboard-tabs"
          tabBarStyle={{
            marginBottom: "24px",
            backgroundColor: "white",
            padding: "8px 16px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          {/* Overview Tab */}
          <TabPane
            tab={
              <span className="text-base font-medium">
                <DashboardOutlined className="mr-2" />
                Overview
              </span>
            }
            key="1"
          >
            <AdminDashboardStats data={dashboardData} />
          </TabPane>

          {/* Charts Tab */}
          <TabPane
            tab={
              <span className="text-base font-medium">
                <BarChartOutlined className="mr-2" />
                Analytics
              </span>
            }
            key="2"
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card className="hover:shadow-xl transition-all duration-300 h-full border-l-4 border-blue-500">
                  <TransactionStatusChart data={dashboardData.transactions} />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card className="hover:shadow-xl transition-all duration-300 h-full border-l-4 border-green-500">
                  <ParkingTicketsChart data={dashboardData.parkingTickets} />
                </Card>
              </Col>
              <Col xs={24}>
                <Card className="hover:shadow-xl transition-all duration-300 border-l-4 border-purple-500">
                  <PaymentComparisonChart
                    cmiData={dashboardData.payments.cmi}
                    fatouratiData={dashboardData.payments.fatourati}
                  />
                </Card>
              </Col>
            </Row>
          </TabPane>

          {/* Financial Tab */}
          {canSeeFinancials && (
          <TabPane
            tab={
              <span className="text-base font-medium">
                <DollarOutlined className="mr-2" />
                Financial
              </span>
            }
            key="3"
          >
            <SystemWalletsFinancial
              systemWallets={dashboardData.systemWallets}
              financial={dashboardData.financial}
            />
            <Card
              title={
                <span>
                  <SyncOutlined className="mr-2" />
                  Réconciliation des infractions
                </span>
              }
              className="mt-4 shadow-md border-l-4 border-orange-500"
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Space>
                  <RangePicker
                    format="YYYY-MM-DD"
                    placeholder={["Date début", "Date fin"]}
                    value={reconcileDates.length ? reconcileDates : null}
                    onChange={(dates) => setReconcileDates(dates || [])}
                  />
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleReconcile}
                    loading={reconcileLoading}
                  >
                    Réconcilier
                  </Button>
                </Space>
                {reconcileResult && (
                  <div style={{ marginTop: 12 }}>
                    <Row gutter={[16, 8]}>
                      <Col xs={24} sm={12}>
                        <Card size="small" title="Résumé">
                          <p>Période: <strong>{reconcileResult.period?.start}</strong> → <strong>{reconcileResult.period?.end}</strong></p>
                          <p>Ajoutées: <strong style={{ color: "#52c41a" }}>{reconcileResult.summary?.total_added ?? 0}</strong></p>
                          <p>Mises à jour: <strong style={{ color: "#1890ff" }}>{reconcileResult.summary?.total_updated ?? 0}</strong></p>
                          {reconcileResult.summary?.counts && (
                            <p>Statuts — En attente: {reconcileResult.summary.counts.pending ?? 0} | Payées: {reconcileResult.summary.counts.paid ?? 0} | Annulées: {reconcileResult.summary.counts.canceled ?? 0}</p>
                          )}
                        </Card>
                      </Col>
                      {reconcileResult.errors?.length > 0 && (
                        <Col xs={24} sm={12}>
                          <Card size="small" title={<span style={{ color: "#f5222d" }}>Erreurs ({reconcileResult.errors.length})</span>}>
                            <ul style={{ margin: 0, paddingLeft: 16, maxHeight: 120, overflowY: "auto" }}>
                              {reconcileResult.errors.map((e, i) => <li key={i} style={{ fontSize: 12 }}>{typeof e === "string" ? e : JSON.stringify(e)}</li>)}
                            </ul>
                          </Card>
                        </Col>
                      )}
                    </Row>
                  </div>
                )}
              </Space>
            </Card>
          </TabPane>
          )}
        </Tabs>
      )}
    </div>
  );
};

export default AdminDashboard;
